import React, { useState } from "react";
import Image from "next/image";
import { Dialog } from "@headlessui/react";
import { ArrowLeftIcon, ArrowRightIcon, XIcon } from "@heroicons/react/outline";
import useTranslation from "next-translate/useTranslation";
import classNames from "lib/classNames";

export default function NodeGalleryContent({ node }) {
  const { t } = useTranslation("common");
  const [isOpen, setIsOpen] = useState(false);
  const [active, setActive] = useState(0);

  const onKeyDownHandler = (event) => {
    switch (event.key) {
      case "ArrowLeft":
        setActive((value) => Math.max(value - 1, 0));
        break;
      case "ArrowRight":
        setActive((value) => Math.min(value + 1, node.field_images.length - 1));
        break;
      default:
        break;
    }
  };

  return (
    <article>
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-8">
        {node.field_images.map((image, index) => (
          <button
            key={image.id}
            type="button"
            className="group relative aspect-w-16 aspect-h-10 overflow-hidden"
            onClick={() => {
              setActive(index);
              setIsOpen(true);
            }}
          >
            <Image
              {...image.resourceIdObjMeta}
              src={image.image_style_uri.small_16by10}
              layout="fill"
              objectFit="cover"
              className="group-hover:scale-110 transition-transform"
            />
          </button>
        ))}
      </div>

      <Dialog
        open={isOpen}
        onClose={() => setIsOpen(false)}
        onKeyDown={onKeyDownHandler}
        className="relative z-50"
      >
        <div className="fixed inset-0 bg-black/30" aria-hidden="true" />
        <div className="fixed inset-0 flex items-center justify-center">
          <Dialog.Panel className="relative container mx-auto px-4 py-8">
            <div className="overflow-hidden aspect-w-16 aspect-h-9">
              {node.field_images.map((image, index) => (
                <figure
                  key={image.id}
                  className={index !== active ? "hidden" : "block"}
                >
                  <Image
                    {...image.resourceIdObjMeta}
                    src={image.image_style_uri.wide}
                    layout="fill"
                    objectFit="contain"
                  />
                  {image.resourceIdObjMeta.title && (
                    <figcaption className="absolute bottom-0 left-0 px-4 py-2 bg-primary text-primary-darker">
                      {image.resourceIdObjMeta.title}
                    </figcaption>
                  )}
                </figure>
              ))}
            </div>
            {node.field_images.length > 1 && (
              <>
                <button
                  type="button"
                  className={classNames(
                    "absolute top-1/2 left-8 -translate-y-1/2 p-2 bg-primary rounded-full z-10",
                    active === 0 && "opacity-50"
                  )}
                  onClick={() => setActive((value) => Math.max(value - 1, 0))}
                  disabled={active === 0}
                >
                  <ArrowLeftIcon className="h-4 w-4" />
                  <span className="sr-only">{t("utils.previous")}</span>
                </button>
                <button
                  type="button"
                  className={classNames(
                    "absolute top-1/2 right-8 -translate-y-1/2 p-2 bg-primary rounded-full z-10",
                    active === node.field_images.length - 1
                  )}
                  onClick={() =>
                    setActive((value) =>
                      Math.min(value + 1, node.field_images.length - 1)
                    )
                  }
                >
                  <ArrowRightIcon className="h-4 w-4" />
                  <span className="sr-only">{t("utils.next")}</span>
                </button>
              </>
            )}
          </Dialog.Panel>
          <button
            type="button"
            className="absolute top-8 right-8 p-2 bg-primary rounded-full"
            onClick={() => setIsOpen(false)}
          >
            <XIcon className="h-4 w-4" />
            <span className="sr-only">{t("utils.close")}</span>
          </button>
        </div>
      </Dialog>
    </article>
  );
}
